/**
 * Line amounts and the order value of a case, per currency (360 simulation P39), and
 * the gap between the list price and the customer's last won price.
 *
 * Pure: the amounts are information for sales to sanity-check a quotation, never a
 * price I2Q sets. Only a usable price of today counts (end-user review F09): status
 * "found" with an amount. A line without a quantity, without such a price, or whose
 * quantity cannot be used in the unit of its price (F03: two boxes priced per piece,
 * F02: 1.5 pieces) has no amount and is counted; currencies are never added up.
 */

import type { QuoteHistoryEvidence } from "@/lib/api";
import type { LinePrice } from "@/lib/mock";

function decimal(value: string | number | null | undefined): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

/** The price of one unit: the list price divided by its price unit (100 per 100, ...). */
export function unitPriceOf(price: LinePrice | null | undefined): number | null {
  const amount = decimal(price?.unitPrice);
  if (amount == null) {
    return null;
  }
  const per = price?.unitQty && price.unitQty > 0 ? price.unitQty : 1;
  return amount / per;
}

/** What a line amount is computed from. */
export interface AmountLine {
  qty: number | null;
  price?: LinePrice | null;
  /** Set when the quantity cannot be used in the unit of the price. */
  qtyIssue?: string | null;
  /** The quantity in the price unit after a verified conversion. */
  priceQty?: number | null;
}

/** True when the line carries a price that may be used today. */
export function hasUsablePrice(line: AmountLine): boolean {
  return line.price?.status === "found" && unitPriceOf(line.price) != null;
}

/** Quantity (in the price unit) times the unit price, or null when either is not usable. */
export function lineAmount(line: AmountLine): { amount: number; currency: string } | null {
  if (!hasUsablePrice(line) || line.qtyIssue) {
    return null;
  }
  const unit = unitPriceOf(line.price);
  const currency = line.price?.currency?.trim().toUpperCase();
  const qty = line.priceQty ?? line.qty;
  if (unit == null || qty == null || !currency) {
    return null;
  }
  return { amount: Math.round(unit * qty * 100) / 100, currency };
}

/**
 * The order value per currency and how many lines have no amount: `withoutPrice` lines
 * have no usable price today, `qtyToCheck` lines have a price but a quantity or unit to
 * check first.
 */
export function caseTotals(lines: readonly AmountLine[]): {
  totals: { currency: string; amount: number }[];
  withoutPrice: number;
  qtyToCheck: number;
} {
  const byCurrency = new Map<string, number>();
  let withoutPrice = 0;
  let qtyToCheck = 0;
  for (const line of lines) {
    const amount = lineAmount(line);
    if (!amount) {
      if (hasUsablePrice(line) && line.qty != null && line.qtyIssue) {
        qtyToCheck += 1;
      } else {
        withoutPrice += 1;
      }
      continue;
    }
    byCurrency.set(amount.currency, (byCurrency.get(amount.currency) ?? 0) + amount.amount);
  }
  return {
    totals: [...byCurrency.entries()]
      .map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }))
      .sort((a, b) => a.currency.localeCompare(b.currency)),
    withoutPrice,
    qtyToCheck,
  };
}

/** "Order value: EUR 1,250.00, USD 80.00 · 2 lines without price · 1 line with a unit to check". */
export function totalsText(totals: ReturnType<typeof caseTotals>): string | null {
  const toCheck = totals.qtyToCheck ?? 0;
  if (totals.totals.length === 0 && totals.withoutPrice === 0 && toCheck === 0) {
    return null;
  }
  const money = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const lines = (count: number) => `${count} ${count === 1 ? "line" : "lines"}`;
  const parts = totals.totals.map((row) => `${row.currency} ${money.format(row.amount)}`);
  const value =
    parts.length > 0
      ? `Order value: ${parts.join(", ")}`
      : totals.withoutPrice > 0
        ? `No line has a usable price yet (${lines(totals.withoutPrice)} without price)`
        : "No line has an amount yet";
  const missing =
    totals.withoutPrice > 0 && parts.length > 0
      ? ` · ${lines(totals.withoutPrice)} without price`
      : "";
  const check = toCheck > 0 ? ` · ${lines(toCheck)} with a quantity or unit to check` : "";
  return `${value}${missing}${check}`;
}

/** The share the price gap may reach before it is flagged. */
export const WON_PRICE_GAP = 0.2;

/**
 * "Last won price differs by 51 %" when the customer's last WON quotation of the item
 * has a unit price more than 20 % away from today's list price (same currency), else null.
 */
export function wonPriceGapText(
  history: QuoteHistoryEvidence | null | undefined,
  price: LinePrice | null | undefined,
): string | null {
  if (!history || (history.won_lost ?? "").trim().toLowerCase() !== "won") {
    return null;
  }
  const list = unitPriceOf(price);
  const wonAmount = decimal(history.sales_price);
  const wonPer = decimal(history.price_unit);
  const sameCurrency =
    (history.currency ?? "").trim().toUpperCase() === (price?.currency ?? "").trim().toUpperCase();
  if (list == null || list <= 0 || wonAmount == null || !sameCurrency) {
    return null;
  }
  const won = wonAmount / (wonPer && wonPer > 0 ? wonPer : 1);
  const gap = Math.abs(won - list) / list;
  return gap > WON_PRICE_GAP ? `Last won price differs by ${Math.round(gap * 100)} %` : null;
}
